import { FC, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AnalysisInProgressModal } from 'src/components/AnalysisInProgressModal'
import { CommentsAnalysisSection } from 'src/charts/ContentAnalysisV2/CommentsAnalysis'
import { ContentAnalysisOverview } from 'src/charts/ContentAnalysisV2/Overview'
import { ContentTopicsSection } from 'src/charts/ContentAnalysisV2/TopicsSection'
import { BaseModal } from 'src/containers/BaseModal'
import { Card } from 'src/components/Card'
import { ConfirmationModalContent } from 'src/components/ConfirmationModalContent'
import { UpgradePlanModal } from 'src/components/UpgradePlanModal'
import en from 'src/constants/en'
import { useContentAnalysisV2Range } from 'src/hooks/useContentAnalysisV2Range'
import {
  AnalysisDraft,
  clearContentAnalysisDraft,
  getContentAnalysisDraft,
  saveContentAnalysisDraft,
} from 'src/localStorage/contentAnalysisDraft'
import { useCreateContentAnalysisMutation } from 'src/reactQuery/useCreateContentAnalysisMutation'
import { useDeleteContentAnalysisMutation } from 'src/reactQuery/useDeleteContentAnalysisMutation'
import { useGetContentAnalysisListQuery } from 'src/reactQuery/useGetContentAnalysisListQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import { useUpdateContentAnalysisMutation } from 'src/reactQuery/useUpdateContentAnalysisMutation'
import { ContentAnalysisApiService } from 'src/services/contentAnalysisApi'
import {
  ContentAnalysisPayload,
  ContentAnalysisPlatform,
  ContentAnalysisType,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysis.interface'
import { StatusModal } from 'src/components/StatusModal'
import { CampaignDetailView } from 'src/pages/ThemeDetail/CampaignDetailHeader'
import { AnalyzePostsByCaptionModal } from './AnalyzePostsByCaptionModal'
import { AnalyzeRecentPostsModal } from './AnalyzeRecentPostsModal'
import { AnalyzeSpecificPostsModal } from './AnalyzeSpecificPostsModal'
import { AnalysisReviewModal } from './AnalysisReviewModal'
import {
  ContentAnalysisHeader,
  ContentHeaderOptions,
} from './ContentAnalysisHeader'
import { ContentComparison } from './ContentComparison'
import { RemoveAllLinksModal } from './RemoveAllLinksModal'
import ContentAnalysisCard from './ContentAnalysisCard'
import { EmptyState } from './EmptyState'
import { HeroSection } from './HeroSection'
import { TabsSection } from './TabsSection'
import classes from './contentAnalysis.module.scss'

type TabType = 'saved' | 'archived' | 'completed' | 'all'

// Helper function to ensure URL has protocol
const normalizeUrl = (url: string): string => {
  if (!url) return url
  const trimmed = url.trim()
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed
  }
  return `https://${trimmed}`
}

const VALID_PLATFORMS = [
  'facebook',
  'instagram',
  'linkedin',
  'tiktok',
  'twitter',
  'youtube',
]

// Validate draft data before submitting
const validateDraft = (draft: AnalysisDraft): string | null => {
  if (draft.recentPosts) {
    const urlsWithContent = draft.recentPosts.urls.filter((u) => u.link)
    for (const url of urlsWithContent) {
      if (!url.platform || !VALID_PLATFORMS.includes(url.platform)) {
        return 'Please select a platform for all Recent Posts URLs'
      }
    }
  }

  if (draft.specificPosts) {
    const urlsWithContent = draft.specificPosts.urls.filter((u) => u.link)
    for (const url of urlsWithContent) {
      if (!url.platform || !VALID_PLATFORMS.includes(url.platform)) {
        return 'Please select a platform for all Specific Posts URLs'
      }
    }
  }

  if (draft.captionSearch) {
    const urlsWithContent = draft.captionSearch.urls.filter((u) => u.link)
    for (const url of urlsWithContent) {
      if (!url.platform || !VALID_PLATFORMS.includes(url.platform)) {
        return 'Please select a platform for all Caption Search URLs'
      }
    }
  }

  return null
}

// Helper function to transform draft data to API payload
const transformDraftToPayload = (
  draft: AnalysisDraft
): ContentAnalysisPayload => {
  const payload: ContentAnalysisPayload = {
    status: 'active',
    title: draft.title,
  }

  if (draft.category) {
    payload.categories = [draft.category]
  }

  if (draft.recentPosts) {
    payload.recentPosts = {
      postCount: draft.recentPosts.postCount,
      urls: draft.recentPosts.urls
        .filter((u) => u.link)
        .map((u) => ({
          pageLink: normalizeUrl(u.link),
          platform: u.platform as ContentAnalysisPlatform,
        })),
    }
  }

  if (draft.specificPosts) {
    payload.specificPosts = {
      urls: draft.specificPosts.urls
        .filter((u) => u.link)
        .map((u) => ({
          platform: u.platform as ContentAnalysisPlatform,
          postLink: normalizeUrl(u.link),
        })),
    }
  }

  if (draft.captionSearch) {
    payload.captionSearch = {
      keyword: draft.captionSearch.keyword,
      urls: draft.captionSearch.urls
        .filter((u) => u.link)
        .map((u) => ({
          pageLink: normalizeUrl(u.link),
          platform: u.platform as ContentAnalysisPlatform,
        })),
    }
  }

  return payload
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getLink = (u: any): string => u.link || u.pageLink || u.postLink || ''

const analysisToAnalysisDraft = (data: ContentAnalysisType): AnalysisDraft => {
  const draft: AnalysisDraft = {
    captionSearch: null,
    category: data.categories?.[0] || '',
    recentPosts: null,
    specificPosts: null,
    title: data.title,
  }

  if (data.recentPosts?.urls?.length) {
    draft.recentPosts = {
      postCount: data.recentPosts.postCount || 10,
      urls: data.recentPosts.urls.map((u) => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        link: getLink(u),
        platform: u.platform,
      })),
    }
  }

  if (data.specificPosts?.urls?.length) {
    draft.specificPosts = {
      urls: data.specificPosts.urls.map((u) => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        link: getLink(u),
        platform: u.platform,
      })),
    }
  }

  if (data.captionSearch?.urls?.length) {
    draft.captionSearch = {
      keyword: data.captionSearch.keyword,
      urls: data.captionSearch.urls.map((u) => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        link: getLink(u),
        platform: u.platform,
      })),
    }
  }

  return draft
}

export const ContentAnalysis: FC = () => {
  const emptyDraft: AnalysisDraft = {
    captionSearch: null,
    recentPosts: null,
    specificPosts: null,
    title: '',
  }
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const [isRecentPostsModalOpen, setIsRecentPostsModalOpen] = useState(false)
  const [isSpecificPostsModalOpen, setIsSpecificPostsModalOpen] =
    useState(false)
  const [isCaptionModalOpen, setIsCaptionModalOpen] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [isInProgressModalOpen, setIsInProgressModalOpen] = useState(false)
  const [isRemoveAllLinksModalOpen, setIsRemoveAllLinksModalOpen] =
    useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [clearLinksModalType, setClearLinksModalType] = useState<
    'recentPosts' | 'specificPosts' | 'captionSearch' | null
  >(null)
  const [reviewModalType, setReviewModalType] = useState<
    'recentPosts' | 'specificPosts' | 'captionSearch' | 'all' | null
  >(null)
  const [activeTab, setActiveTab] = useState<TabType>('saved')
  const [searchInput, setSearchInput] = useState<string>('')
  const [analysisDraft, setAnalysisDraft] = useState<AnalysisDraft>({
    captionSearch: null,
    recentPosts: null,
    specificPosts: null,
    title: '',
  })
  const [analysisToDelete, setAnalysisToDelete] =
    useState<ContentAnalysisType | null>(null)
  const [analysisToRename, setAnalysisToRename] =
    useState<ContentAnalysisType | null>(null)
  const [editingAnalysisId, setEditingAnalysisId] = useState<string | null>(
    null
  )
  const [editDraft, setEditDraft] = useState<AnalysisDraft>(emptyDraft)

  // Load draft from localStorage on mount
  useEffect(() => {
    const savedDraft = getContentAnalysisDraft()
    if (savedDraft) {
      setAnalysisDraft(savedDraft)
    }
  }, [])

  // Active draft: edit draft when editing, creation draft otherwise
  const isEditing = !!editingAnalysisId
  const activeDraft = isEditing ? editDraft : analysisDraft
  const updateActiveDraft = (newDraft: AnalysisDraft) => {
    if (isEditing) {
      setEditDraft(newDraft)
    } else {
      setAnalysisDraft(newDraft)
      saveContentAnalysisDraft(newDraft)
    }
  }

  // Check if any creation draft data exists (for setup cards only)
  const hasDraftData =
    analysisDraft.recentPosts !== null ||
    analysisDraft.specificPosts !== null ||
    analysisDraft.captionSearch !== null

  // Handlers for modal onNext callbacks
  const handleRecentPostsNext = (data: {
    analysisTitle: string
    category: string
    postCount: number
    urls: Array<{ id: string; link: string; platform: string }>
  }) => {
    updateActiveDraft({
      ...activeDraft,
      category: data.category,
      recentPosts: { postCount: data.postCount, urls: data.urls },
      title: data.analysisTitle,
    })
    setIsRecentPostsModalOpen(false)
    setReviewModalType('recentPosts')
    setIsReviewModalOpen(true)
  }

  const handleSpecificPostsNext = (data: {
    analysisTitle: string
    category: string
    urls: Array<{ id: string; link: string; platform: string }>
  }) => {
    updateActiveDraft({
      ...activeDraft,
      category: data.category,
      specificPosts: { urls: data.urls },
      title: data.analysisTitle,
    })
    setIsSpecificPostsModalOpen(false)
    setReviewModalType('specificPosts')
    setIsReviewModalOpen(true)
  }

  const handleCaptionSearchNext = (data: {
    analysisTitle: string
    category: string
    keywords: string[]
    resultsFrom: string
    urls: Array<{ id: string; link: string; platform: string }>
  }) => {
    updateActiveDraft({
      ...activeDraft,
      captionSearch: { keyword: data.keywords.join(', '), urls: data.urls },
      category: data.category,
      title: data.analysisTitle,
    })
    setIsCaptionModalOpen(false)
    setReviewModalType('captionSearch')
    setIsReviewModalOpen(true)
  }

  // Review modal handlers
  const handleClearSection = (
    type: 'recentPosts' | 'specificPosts' | 'captionSearch'
  ) => {
    updateActiveDraft({ ...activeDraft, [type]: null })
  }

  const handleClearAll = () => {
    if (isEditing) {
      setEditDraft(emptyDraft)
    } else {
      setAnalysisDraft(emptyDraft)
      clearContentAnalysisDraft()
    }
    setIsReviewModalOpen(false)
  }

  const handleEditSection = (
    type: 'recentPosts' | 'specificPosts' | 'captionSearch'
  ) => {
    setIsReviewModalOpen(false)
    if (type === 'recentPosts') {
      setIsRecentPostsModalOpen(true)
    } else if (type === 'specificPosts') {
      setIsSpecificPostsModalOpen(true)
    } else {
      setIsCaptionModalOpen(true)
    }
  }

  const { mutate: createContentAnalysis, isPending: isCreating } =
    useCreateContentAnalysisMutation({
      onSuccess: () => {
        handleClearAll()
        setIsInProgressModalOpen(true)
      },
    })

  const { mutate: updateContentAnalysis, isPending: isUpdating } =
    useUpdateContentAnalysisMutation({
      onSuccess: () => {
        setEditDraft(emptyDraft)
        setEditingAnalysisId(null)
        setIsReviewModalOpen(false)
      },
    })

  const { mutate: deleteContentAnalysis } = useDeleteContentAnalysisMutation()

  const isSubmitting = isCreating || isUpdating

  const handleSubmitAnalysis = () => {
    const validationError = validateDraft(activeDraft)
    if (validationError) {
      toast.error(validationError)
      return
    }
    const payload = transformDraftToPayload(activeDraft)
    if (editingAnalysisId) {
      updateContentAnalysis({ id: editingAnalysisId, payload })
    } else {
      createContentAnalysis(payload)
    }
  }

  // Fetch user features from backend to check access
  const { data: userFeatures, isLoading: featuresLoading } =
    useGetUserFeaturesQuery()

  // Fetch content analysis list
  const [searchParams, setSearchParams] = useSearchParams()
  const listFromDate = searchParams.get('from') || undefined
  const listToDate = searchParams.get('to') || undefined
  const {
    data: contentAnalysisList,
    isLoading: listLoading,
    refetch,
  } = useGetContentAnalysisListQuery({
    fromDate: listFromDate,
    toDate: listToDate,
  })

  const hasAccess = userFeatures?.data?.contentAnalysisEnabled ?? false
  const v2Range = useContentAnalysisV2Range()

  // Which view is in the URL, not in state: a reload used to drop the reader
  // back on the overview, and the comparison had no address to open directly or
  // to send anyone. ?view=comparison opens it; nothing means the overview.
  const view: CampaignDetailView =
    searchParams.get('view') === 'comparison' ? 'comparison' : 'general'

  const setView = (next: CampaignDetailView) => {
    const params = new URLSearchParams(searchParams)

    if (next === 'comparison') params.set('view', 'comparison')
    else params.delete('view')

    setSearchParams(params, { replace: true })
  }

  // Every analysis the account has, not the tab's filtered slice: which tab the
  // overview happens to be on says nothing about what may be compared.
  const comparisonCampaigns = (contentAnalysisList?.data || []).map((item) => ({
    id: item._id,
    name: item.title,
  }))

  // The comparison screens each name their own way back — the list to the
  // overview, a report to the list — so they render the header rather than
  // being rendered under one.
  const renderHeader = (options?: ContentHeaderOptions) => (
    <ContentAnalysisHeader
      embedded={options?.embedded}
      onBack={options?.onBack}
      onViewChange={setView}
      trail={options?.trail}
      view={view}
    />
  )

  // Filter content analyses based on active tab
  const filteredAnalyses = (contentAnalysisList?.data || []).filter((item) => {
    let matchesTab = false
    if (activeTab === 'saved') {
      matchesTab = item.status === 'active'
    } else if (activeTab === 'archived') {
      matchesTab = item.status === 'draft'
    } else if (activeTab === 'completed') {
      matchesTab = item.status === 'completed' || item.status === 'paused'
    } else {
      matchesTab = true
    }

    const matchesSearch =
      !searchInput.trim() ||
      item.title?.toLowerCase().includes(searchInput.toLowerCase())

    return matchesTab && matchesSearch
  })

  // Count items for each tab
  const savedCount = (contentAnalysisList?.data || []).filter(
    (item) => item.status === 'active'
  ).length
  const archivedCount = (contentAnalysisList?.data || []).filter(
    (item) => item.status === 'draft'
  ).length
  const completedCount = (contentAnalysisList?.data || []).filter(
    (item) => item.status === 'completed' || item.status === 'paused'
  ).length
  const allCount = (contentAnalysisList?.data || []).length

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
  }

  // Show loading while checking user access
  if (featuresLoading) {
    return (
      <div className={classes.contentAnalysisContainer}>
        <div className={classes.content}>
          <div className={classes.emptyState}>
            <p>Checking access permissions...</p>
          </div>
        </div>
      </div>
    )
  }

  // Show access denied if user doesn't have access
  if (!hasAccess) {
    return (
      <>
        <div className={classes.disabledWrapper}>
          <EmptyState
            onEnableClick={() => setIsUpgradeModalOpen(true)}
            type="disabled"
          />
        </div>
        <UpgradePlanModal
          isOpen={isUpgradeModalOpen}
          setIsOpen={setIsUpgradeModalOpen}
        />
      </>
    )
  }

  if (view === 'comparison') {
    return (
      <div className={classes.comparisonContainer}>
        <ContentComparison
          campaigns={comparisonCampaigns}
          header={renderHeader}
          onExitComparison={() => setView('general')}
        />
      </div>
    )
  }

  // Enabled state with full UI
  return (
    <div className={classes.contentAnalysisContainer}>
      {renderHeader()}

      {/* Across every analysis the account has: no analysisIds. */}
      <div className={classes.overviewSections}>
        <ContentAnalysisOverview
          campaigns={
            listLoading ? undefined : { active: savedCount, total: allCount }
          }
          range={v2Range}
          variant="list"
        />
        <ContentTopicsSection range={v2Range} showCampaign />
        <CommentsAnalysisSection
          campaigns={comparisonCampaigns}
          range={v2Range}
        />
      </div>

      {/* Hero Section with Cards */}
      <HeroSection
        analysisDraft={analysisDraft}
        hasDraftData={hasDraftData}
        onCaptionClick={() => setIsCaptionModalOpen(true)}
        onCaptionReviewClick={() => {
          setReviewModalType('captionSearch')
          setIsReviewModalOpen(true)
        }}
        onRecentPostsClick={() => setIsRecentPostsModalOpen(true)}
        onRecentPostsReviewClick={() => {
          setReviewModalType('recentPosts')
          setIsReviewModalOpen(true)
        }}
        onSpecificPostsClick={() => setIsSpecificPostsModalOpen(true)}
        onSpecificPostsReviewClick={() => {
          setReviewModalType('specificPosts')
          setIsReviewModalOpen(true)
        }}
      />

      {/* Tabs Section */}
      <TabsSection
        activeTab={activeTab}
        allCount={allCount}
        archivedCount={archivedCount}
        completedCount={completedCount}
        onSearchChange={handleSearchChange}
        onTabChange={setActiveTab}
        savedCount={savedCount}
        searchValue={searchInput}
      />

      {/* Content List */}
      <div className={classes.savedContentArea}>
        {listLoading ? (
          <EmptyState isLoading />
        ) : filteredAnalyses.length === 0 ? (
          <EmptyState />
        ) : (
          <div className={classes.contentGrid}>
            {filteredAnalyses.map((analysis) => (
              <ContentAnalysisCard
                data={analysis}
                key={analysis._id}
                onActivate={async () => {
                  try {
                    await ContentAnalysisApiService.update(analysis._id, {
                      ...analysis,
                      status: 'active',
                    } as unknown as ContentAnalysisType)
                    refetch()
                    toast.success('Analysis activated successfully')
                  } catch {
                    toast.error('Failed to activate analysis')
                  }
                }}
                onComplete={async () => {
                  try {
                    await ContentAnalysisApiService.update(analysis._id, {
                      ...analysis,
                      status: 'completed',
                    } as unknown as ContentAnalysisType)
                    refetch()
                    toast.success('Analysis marked as completed')
                  } catch {
                    toast.error('Failed to complete analysis')
                  }
                }}
                onDelete={() => setAnalysisToDelete(analysis)}
                onDuplicate={async () => {
                  try {
                    const response = await ContentAnalysisApiService.getById(
                      analysis._id
                    )
                    const { _id, createdAt, updatedAt, ...duplicateData } =
                      response.data
                    void _id
                    void createdAt
                    void updatedAt
                    const newAnalysis = {
                      ...duplicateData,
                      title: `${duplicateData.title} (Copy)`,
                    }
                    await ContentAnalysisApiService.create(newAnalysis)
                    refetch()
                    toast.success('Analysis duplicated successfully')
                  } catch {
                    toast.error('Failed to duplicate analysis')
                  }
                }}
                onEdit={async () => {
                  try {
                    const response = await ContentAnalysisApiService.getById(
                      analysis._id
                    )
                    const draft = analysisToAnalysisDraft(response.data)
                    setEditDraft(draft)
                    setEditingAnalysisId(analysis._id)
                    setIsReviewModalOpen(true)
                  } catch {
                    toast.error('Failed to load analysis data')
                  }
                }}
                onRename={() => setAnalysisToRename(analysis)}
              />
            ))}
          </div>
        )}
      </div>

      <AnalyzeRecentPostsModal
        initialData={
          activeDraft.recentPosts
            ? {
                analysisTitle: activeDraft.title || '',
                postCount: activeDraft.recentPosts.postCount,
                urls: activeDraft.recentPosts.urls,
              }
            : null
        }
        isOpen={isRecentPostsModalOpen}
        onClearAllLinks={() => {
          setClearLinksModalType('recentPosts')
          setIsRecentPostsModalOpen(false)
          setIsRemoveAllLinksModalOpen(true)
        }}
        onClose={() => setIsRecentPostsModalOpen(false)}
        onNext={handleRecentPostsNext}
      />

      <RemoveAllLinksModal
        isOpen={isRemoveAllLinksModalOpen}
        onCancel={() => {
          setIsRemoveAllLinksModalOpen(false)
          if (clearLinksModalType === 'recentPosts') {
            setIsRecentPostsModalOpen(true)
          } else if (clearLinksModalType === 'specificPosts') {
            setIsSpecificPostsModalOpen(true)
          } else if (clearLinksModalType === 'captionSearch') {
            setIsCaptionModalOpen(true)
          }
        }}
        onConfirm={() => {
          // Clear the appropriate draft data based on modal type
          if (clearLinksModalType === 'recentPosts') {
            updateActiveDraft({
              ...activeDraft,
              recentPosts: {
                postCount: 0,
                urls: [{ id: '1', link: '', platform: '' }],
              },
            })
          } else if (clearLinksModalType === 'specificPosts') {
            updateActiveDraft({
              ...activeDraft,
              specificPosts: {
                urls: [{ id: '1', link: '', platform: '' }],
              },
            })
          } else if (clearLinksModalType === 'captionSearch') {
            updateActiveDraft({
              ...activeDraft,
              captionSearch: {
                keyword: '',
                urls: [{ id: '1', link: '', platform: '' }],
              },
            })
          }
          // Close RemoveAllLinksModal first
          setIsRemoveAllLinksModalOpen(false)
          // Open SuccessModal after a brief delay to ensure clean transition
          setTimeout(() => {
            setIsSuccessModalOpen(true)
          }, 50)
        }}
      />

      <StatusModal
        isOpen={isSuccessModalOpen}
        message="All added URLs were successfully deleted from the list."
        onClose={() => {
          setIsSuccessModalOpen(false)
          if (clearLinksModalType === 'recentPosts') {
            setIsRecentPostsModalOpen(true)
          } else if (clearLinksModalType === 'specificPosts') {
            setIsSpecificPostsModalOpen(true)
          } else if (clearLinksModalType === 'captionSearch') {
            setIsCaptionModalOpen(true)
          }
          setClearLinksModalType(null)
        }}
        subMessage="Closing tab in"
        title="All links have been removed"
      />

      <AnalyzeSpecificPostsModal
        initialData={
          activeDraft.specificPosts
            ? {
                analysisTitle: activeDraft.title || '',
                urls: activeDraft.specificPosts.urls,
              }
            : null
        }
        isOpen={isSpecificPostsModalOpen}
        onClearAllLinks={() => {
          setClearLinksModalType('specificPosts')
          setIsSpecificPostsModalOpen(false)
          setIsRemoveAllLinksModalOpen(true)
        }}
        onClose={() => setIsSpecificPostsModalOpen(false)}
        onNext={handleSpecificPostsNext}
      />

      <AnalyzePostsByCaptionModal
        initialData={
          activeDraft.captionSearch
            ? {
                analysisTitle: activeDraft.title || '',
                keywords: activeDraft.captionSearch.keyword
                  ? activeDraft.captionSearch.keyword.split(', ')
                  : [],
                resultsFrom: '',
                urls: activeDraft.captionSearch.urls,
              }
            : null
        }
        isOpen={isCaptionModalOpen}
        onClearAllLinks={() => {
          setClearLinksModalType('captionSearch' as any)
          setIsCaptionModalOpen(false)
          setIsRemoveAllLinksModalOpen(true)
        }}
        onClose={() => setIsCaptionModalOpen(false)}
        onNext={handleCaptionSearchNext}
      />

      <AnalysisReviewModal
        draft={
          reviewModalType === 'recentPosts'
            ? {
                ...activeDraft,
                captionSearch: null,
                specificPosts: null,
              }
            : reviewModalType === 'specificPosts'
              ? {
                  ...activeDraft,
                  captionSearch: null,
                  recentPosts: null,
                }
              : reviewModalType === 'captionSearch'
                ? {
                    ...activeDraft,
                    recentPosts: null,
                    specificPosts: null,
                  }
                : activeDraft
        }
        isOpen={isReviewModalOpen}
        isSubmitting={isSubmitting}
        onCategoryChange={(category) =>
          updateActiveDraft({ ...activeDraft, category })
        }
        onClear={handleClearSection}
        onClearAll={handleClearAll}
        onClose={() => {
          setIsReviewModalOpen(false)
          setEditingAnalysisId(null)
          setEditDraft(emptyDraft)
          setReviewModalType(null)
        }}
        onEdit={handleEditSection}
        onSubmit={handleSubmitAnalysis}
      />

      <BaseModal isOpen={!!analysisToDelete}>
        <ConfirmationModalContent
          onCancel={() => setAnalysisToDelete(null)}
          onConfirm={() => {
            if (analysisToDelete?._id) {
              deleteContentAnalysis(analysisToDelete._id)
              setAnalysisToDelete(null)
            }
          }}
          title={`${en.areYouSureYouWantToDelete} ${analysisToDelete?.title}?`}
        />
      </BaseModal>

      <AnalysisInProgressModal
        isOpen={isInProgressModalOpen}
        onClose={() => setIsInProgressModalOpen(false)}
      />

      <BaseModal isOpen={!!analysisToRename}>
        <Card>
          <div style={{ minWidth: '320px', padding: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              Analysis Title
            </label>
            <input
              defaultValue={analysisToRename?.title}
              id="rename-input"
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                padding: '10px 12px',
                width: '100%',
              }}
              type="text"
            />
            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px',
              }}
            >
              <button
                onClick={() => setAnalysisToRename(null)}
                style={{
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  padding: '10px 16px',
                }}
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const input = document.getElementById(
                    'rename-input'
                  ) as HTMLInputElement
                  const newTitle = input?.value?.trim()
                  if (newTitle && analysisToRename?._id) {
                    try {
                      await ContentAnalysisApiService.update(
                        analysisToRename._id,
                        {
                          ...analysisToRename,
                          title: newTitle,
                        }
                      )
                      refetch()
                      setAnalysisToRename(null)
                      toast.success('Analysis renamed successfully')
                    } catch {
                      toast.error('Failed to rename analysis')
                    }
                  }
                }}
                style={{
                  background: '#8080FF',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  padding: '10px 16px',
                }}
                type="button"
              >
                Rename
              </button>
            </div>
          </div>
        </Card>
      </BaseModal>
    </div>
  )
}
